import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export interface WorldCupGroupCreatedScreenStyleOptions {
  compact?: boolean;
  bottomInset?: number;
}

export const getWorldCupGroupCreatedScreenStyles = (
  theme: Theme,
  options?: WorldCupGroupCreatedScreenStyleOptions,
) => {
  const compact = options?.compact ?? false;
  const bottomInset = options?.bottomInset ?? 0;
  const bottomPad = Math.max(12, bottomInset + 8);

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    gradientBackdrop: {
      ...StyleSheet.absoluteFillObject,
    },
    bokehLarge: {
      position: 'absolute',
      width: 180,
      height: 180,
      borderRadius: 90,
      backgroundColor: theme.primaryLight,
      opacity: 0.12,
      top: -40,
      right: -50,
    },
    bokehSmall: {
      position: 'absolute',
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: theme.accentLight,
      opacity: 0.18,
      bottom: 120,
      left: -30,
    },
    bokehAccent: {
      position: 'absolute',
      width: 90,
      height: 90,
      borderRadius: 45,
      backgroundColor: theme.primary,
      opacity: 0.06,
      top: 280,
      right: -20,
    },
    scroll: { flex: 1 },
    scrollContent: {
      flexGrow: 1,
      paddingHorizontal: compact ? 16 : 20,
      paddingTop: compact ? 8 : 12,
      paddingBottom: bottomPad + 70,
      zIndex: 1,
      ...(Platform.OS === 'web' && {
        maxWidth: 480,
        width: '100%' as never,
        alignSelf: 'center' as const,
      }),
    },

    // ─── Hero card ───────────────────────────────────────────────────
    heroCard: {
      borderRadius: 22,
      borderWidth: 1,
      borderColor: `${theme.primary}33` as never,
      overflow: 'hidden',
      position: 'relative',
      paddingVertical: compact ? 16 : 20,
      paddingHorizontal: 20,
      marginBottom: compact ? 10 : 14,
      alignItems: 'center',
    },
    heroGradient: { ...StyleSheet.absoluteFillObject },
    confettiContainer: { ...StyleSheet.absoluteFillObject },
    confettiBit: { position: 'absolute', opacity: 0.8 },
    mascotGlow: {
      position: 'absolute',
      width: 140,
      height: 140,
      borderRadius: 70,
      backgroundColor: theme.accent,
      opacity: 0.12,
    },
    mascotImage: {
      width: compact ? 90 : 110,
      height: compact ? 90 : 110,
    },
    badge: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.accent,
      letterSpacing: 1.6,
      textTransform: 'uppercase',
      marginTop: 6,
      marginBottom: 4,
    },
    headline: {
      fontSize: compact ? 20 : 24,
      fontWeight: '900',
      color: theme.gray[900],
      letterSpacing: -0.6,
      textAlign: 'center',
      lineHeight: compact ? 26 : 30,
      marginBottom: 4,
    },
    subtitle: {
      fontSize: compact ? 12 : 13,
      fontWeight: '500',
      color: theme.gray[500],
      textAlign: 'center',
      lineHeight: compact ? 17 : 19,
    },

    // ─── Crest + summary row ─────────────────────────────────────────
    crestSummaryCard: {
      borderRadius: 16,
      borderWidth: 1,
      borderColor: theme.gray[200],
      backgroundColor: theme.background.card,
      paddingVertical: 12,
      paddingHorizontal: 14,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      marginBottom: compact ? 10 : 12,
    },
    crestSummaryInfo: { flex: 1, minWidth: 0 },
    crestSummaryName: {
      fontSize: 14,
      fontWeight: '800',
      color: theme.gray[900],
      letterSpacing: -0.2,
    },
    crestSummaryTagline: {
      fontSize: 11,
      color: theme.gray[500],
      marginTop: 2,
    },
    crestSummaryBuyInWrap: { alignItems: 'flex-end' },
    crestSummaryBuyInLabel: {
      fontSize: 8,
      fontWeight: '800',
      color: theme.gray[400],
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    crestSummaryBuyInCoinRow: { flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 2 },
    crestSummaryBuyInCoin: { width: 14, height: 14 },
    crestSummaryBuyInAmount: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.accent,
    },
    crestSummaryBuyInFree: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.accent,
      marginTop: 2,
    },

    // ─── Invite card ─────────────────────────────────────────────────
    inviteCard: {
      borderRadius: 20,
      borderWidth: 1,
      borderColor: `${theme.primary}55` as never,
      overflow: 'hidden',
      marginBottom: compact ? 10 : 12,
    },
    inviteGradient: { ...StyleSheet.absoluteFillObject },
    inviteGlow: {
      position: 'absolute',
      top: -20,
      right: -20,
      width: 100,
      height: 100,
      borderRadius: 50,
      backgroundColor: theme.accent,
      opacity: 0.08,
    },
    inviteInner: {
      paddingVertical: compact ? 14 : 18,
      paddingHorizontal: compact ? 14 : 18,
    },
    codeLabel: {
      fontSize: 9,
      fontWeight: '800',
      color: theme.primary,
      letterSpacing: 1.4,
      textTransform: 'uppercase',
      marginBottom: 6,
    },
    codeText: {
      fontSize: compact ? 26 : 30,
      fontWeight: '900',
      color: theme.gray[900],
      letterSpacing: compact ? 3 : 4,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
      marginBottom: 12,
    },
    shareRow: {
      flexDirection: 'row',
      gap: 8,
    },
    shareButton: {
      flex: 1,
      paddingVertical: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.gray[200],
      backgroundColor: 'rgba(255,255,255,0.06)',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
      gap: 5,
    },
    shareButtonPrimary: {
      borderWidth: 0,
      overflow: 'hidden',
    },
    shareButtonGradient: {
      ...StyleSheet.absoluteFillObject,
    },
    shareButtonText: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: theme.gray[900],
    },
    shareButtonTextPrimary: {
      color: theme.onPrimary,
    },
    shareButtonPressed: { opacity: 0.78 },
    shareButtonHover: { opacity: 0.88 },

    // ─── Sticky CTA ──────────────────────────────────────────────────
    stickyCtaWrap: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: bottomInset,
      paddingHorizontal: 16,
      paddingTop: 28,
    },
    stickyCtaFade: {
      ...StyleSheet.absoluteFillObject,
    },
    stickyCtaButton: {
      borderRadius: 16,
      overflow: 'hidden',
      marginBottom: 6,
    },
    stickyCtaPressed: { opacity: 0.88 },
    stickyCtaGradient: {
      paddingVertical: 15,
      alignItems: 'center',
      justifyContent: 'center',
    },
    stickyCtaText: {
      fontSize: 15,
      fontWeight: '900',
      color: theme.onPrimary,
      letterSpacing: 0.4,
    },
    tertiaryCta: {
      paddingVertical: 8,
      alignItems: 'center',
    },
    tertiaryCtaPressed: { opacity: 0.6 },
    tertiaryCtaText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray[600],
    },
  });
};
